import { AuthEnums, AutomationRuleEnums } from "models/enums";
import { AutomationRuleTypeHelper } from "utils/helpers";
import {
  WintSuperkollAPIModelCustomerAutomationsWintCard as ReceiptDraft,
  WintSuperkollAPIModelCustomerAutomationsCommon as Common,
  WintUtilsWebApi,
  WintSuperkollAPIModel,
} from "wint-api-client/dist/types";
import { AutomationRuleFormTypes } from "models/formTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { useCreateReceiptDraftFormHelpers } from "components/automationRule/forms/receiptDraft";
import { AutomationRuleListItem } from "models/propTypes/automationRulePropTypes";
import { AxiosResponse } from "axios";
import useAPIClientHelper from "./useAPIClientHelper";

function useAutomationRuleService() {
  const { apiClientInstance } = useAPIClientHelper();
  const { receiptCategories } = useCreateReceiptDraftFormHelpers();

  const createReceiptDraftRuleApi = (rule: ReceiptDraft.WintCardAutomationRulePost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).ReceiptAutomationRuleClient.CreateWintCardRule(rule);
  };

  const getReceiptDraftRuleApi = (id: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).ReceiptAutomationRuleClient.GetWintCardRule(id);
  };

  const updateReceiptDraftRuleApi = (id: string, rule: ReceiptDraft.WintCardAutomationRulePut) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).ReceiptAutomationRuleClient.UpdateWintCardRule(id, rule);
  };

  const deleteReceitpDraftRule = (id: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).ReceiptAutomationRuleClient.DeleteWintCardRule(id);
  };

  const convertReceiptDraftFormToApiPost = (
    form: AutomationRuleFormTypes.CreateReceiptDraftRule,
    associatedReceiptId?: number
  ): ReceiptDraft.WintCardAutomationRulePost => {
    return {
      ruleType: Common.RuleType.Receipts,
      ruleSubType: Common.RuleSubType.WintCard,
      paymentMethodId: form.paymentMethodId ?? 0,
      receiptCategoryId: (form.category?.id as number) ?? 0,
      supplierName: form.supplier?.value ?? "",
      minAmount: form.minimumAmount,
      maxAmount: form.maximumAmount,
      maxEventsForTheMonth: form.maxAutomatedEventsPerMonth,
      dimensions: form.dimensions?.filter((d) => d.dimensionId)?.map((d) => d.dimensionId!),
      active: form.isActive,
      receiptId: associatedReceiptId,
    };
  };

  const convertReceiptDraftFormToApiPut = (
    form: AutomationRuleFormTypes.CreateReceiptDraftRule
  ): ReceiptDraft.WintCardAutomationRulePut => {
    return {
      paymentMethodId: form.paymentMethodId ?? 0,
      receiptCategoryId: (form.category?.id as number) ?? 0,
      supplierName: form.supplier?.value ?? "",
      minAmount: form.minimumAmount,
      maxAmount: form.maximumAmount,
      maxEventsForTheMonth: form.maxAutomatedEventsPerMonth,
      dimensions: form.dimensions?.filter((d) => d.dimensionId)?.map((d) => d.dimensionId!),
      active: form.isActive,
    };
  };

  const convertReceiptDraftApiToForm = (
    receiptDraft: ReceiptDraft.WintCardAutomationRule
  ): AutomationRuleFormTypes.AutomationRuleCreate => {
    return {
      type: AutomationRuleEnums.AutomationRuleType.ReceiptDraft,
      receiptDraft: {
        paymentMethodId: receiptDraft.paymentMethodId,
        category: receiptCategories?.find((c) => c.id === receiptDraft.receiptCategoryId),
        supplier: { value: receiptDraft.supplierName } as AutocompleteListItem,
        minimumAmount: receiptDraft.minAmount,
        maximumAmount: receiptDraft.maxAmount,
        maxAutomatedEventsPerMonth: receiptDraft.maxEventsForTheMonth,
        isActive: receiptDraft.active,
        dimensions: receiptDraft.dimensions?.map(
          (d): WintSuperkollAPIModel.DimensionData => ({ dimensionId: d, amount: 1 })
        ),
      },
    };
  };

  const createReceiptDraftRule = (form: AutomationRuleFormTypes.AutomationRuleCreate, associatedReceiptId?: number) => {
    const receiptDraftRulePost = convertReceiptDraftFormToApiPost(form.receiptDraft!, associatedReceiptId);
    return createReceiptDraftRuleApi(receiptDraftRulePost);
  };

  const updateReceiptDraftRule = (id: string, form: AutomationRuleFormTypes.AutomationRuleCreate) => {
    const receiptDraftRulePut = convertReceiptDraftFormToApiPut(form.receiptDraft!);
    return updateReceiptDraftRuleApi(id, receiptDraftRulePut);
  };

  const getReceiptDraftRule = (id: string) => {
    return getReceiptDraftRuleApi(id).then((rule) => convertReceiptDraftApiToForm(rule.data));
  };

  const filter = (filter: Common.AutomationRuleFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).AutomationRuleClient.Get(filter);
  };

  const reportFilter = (baseFilter: WintUtilsWebApi.Filter, formFilter?: AutomationRuleFormTypes.Filter) => {
    return filter({
      ...baseFilter,
      ...reportToAPIFilter(formFilter),
    });
  };

  const convertReportToListItems = (
    ruleListResponse: AxiosResponse<WintUtilsWebApi.ListResponse<Common.AutomationRuleListItem>>
  ): AxiosResponse<WintUtilsWebApi.ListResponse<AutomationRuleListItem>> => {
    const { data, ...rest } = ruleListResponse;

    return {
      ...rest,
      data: {
        ...data,
        items: data.items?.map((r) => ({ ...r, moreActionColumn: null })),
        summaryItem: data.summaryItem ? { ...data.summaryItem, moreActionColumn: null } : undefined,
      },
    };
  };

  const reportToAPIFilter = (
    formFilter?: AutomationRuleFormTypes.Filter
  ): Omit<Common.AutomationRuleFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      maxTotalProcessedEvents: formFilter?.totalProcessedEvents?.highest,
      minTotalProcessedEvents: formFilter?.totalProcessedEvents?.lowest,
      createdFromDate: formFilter?.createdDate?.start,
      createdToDate: formFilter?.createdDate?.end,
      active: formFilter?.active !== undefined ? Boolean(formFilter.active) : undefined,
      ruleSubTypes: formFilter?.type?.map((t) =>
        AutomationRuleTypeHelper.getApiRuleSubTypeByRuleTypeEnum(t.id as AutomationRuleEnums.AutomationRuleType)
      ),
    };
  };

  const activateReceiptDraftRule = (id: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).ReceiptAutomationRuleClient.ActivateWintCardRule(id);
  };

  const deactivateReceiptDraftRule = (id: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).ReceiptAutomationRuleClient.DeactivateWintCardRule(id);
  };

  return {
    getReceiptDraftRule,
    createReceiptDraftRule,
    updateReceiptDraftRule,
    reportFilter,
    reportToAPIFilter,
    deleteReceitpDraftRule,
    convertReportToListItems,
    activateReceiptDraftRule,
    deactivateReceiptDraftRule,
  };
}

export default useAutomationRuleService;
